import { ReactNode } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface MetricsCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: ReactNode;
  sparklineData?: number[];
  trend?: 'up' | 'down';
}

export default function MetricsCard({
  title,
  value,
  change,
  icon,
  sparklineData,
  trend
}: MetricsCardProps) {
  const hasPositiveChange = change !== undefined && change >= 0;
  const showSparkline = sparklineData && sparklineData.length > 0;

  // Transform sparkline data for recharts
  const chartData = sparklineData?.map((value, index) => ({
    index,
    value
  })) || [];

  return (
    <div className="bg-recipe-card border border-recipe-border rounded-xl p-6 hover:border-recipe-accent/30 transition-all duration-300 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-recipe-accent/10 text-recipe-accent group-hover:bg-recipe-accent/20 transition-colors">
            {icon}
          </div>
          <div>
            <p className="text-sm text-recipe-text-muted font-medium">{title}</p>
            <p className="text-2xl font-bold text-recipe-text mt-1">{value}</p>
          </div>
        </div>

        {change !== undefined && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${
            hasPositiveChange
              ? 'bg-green-500/10 text-green-400'
              : 'bg-red-500/10 text-red-400'
          }`}>
            {hasPositiveChange ? (
              <TrendingUp className="w-3.5 h-3.5" />
            ) : (
              <TrendingDown className="w-3.5 h-3.5" />
            )}
            <span className="text-xs font-semibold">
              {Math.abs(change)}%
            </span>
          </div>
        )}
      </div>

      {showSparkline && (
        <div className="h-12 -mb-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke="#F5D547"
                strokeWidth={2}
                dot={false}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {trend && !showSparkline && (
        <div className="flex items-center gap-2 mt-2">
          {trend === 'up' ? (
            <TrendingUp className="w-4 h-4 text-green-400" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-400" />
          )}
          <span className="text-xs text-recipe-text-muted">
            {trend === 'up' ? 'Trending upward' : 'Trending downward'}
          </span>
        </div>
      )}
    </div>
  );
}
